/**
 * Form-state Zod schema for the GoodsItem detail view.
 *
 * Phase 1: Header fields + Classification tab.
 * Phase 2: Customs Valuation + Packaging tabs.
 * Phase 3 will add Regulations tab fields.
 *
 * @see UX Mocks: GoodsItem detail drawer
 * @see CDS spec: https://quip-amazon.com/haRbADR5yV7P/CuDe-Line-level-details
 */
import { z } from 'zod';
import { entityRefSchema } from './common';

/** Product reference stored in form state. id+version required for API (IdVersionInput). */
export const productRefSchema = z.object({
  id: z.string(),
  version: z.number(),
  clientIdentifier: z.string().nullish(),
});

export const classificationEntrySchema = z.object({
  identification: z.string().nullish(),
  identificationType: z.string().nullish(),
  sequence: z.coerce.number().int().nullish(),
});

export const valuationAdjustmentEntrySchema = z.object({
  type: z.string().nullish(),
  amount: z.coerce.number().nullish(),
  currency: z.string().nullish(),
  percentage: z.coerce.number().nullish(),
});

export const importControlEntrySchema = z.object({
  code: z.string().nullish(),
  reference: z.string().nullish(),
});

export const goodsItemDetailLiveSchema = z.object({
  // Header
  product: productRefSchema.nullish(),
  productSku: z.string().nullish(),
  natureOfTransaction: z.string().nullish(),
  quantity: z.coerce.number().nullish(),
  quantityUnit: z.string().nullish(),
  totalValue: z.coerce.number().nullish(),

  // Classification tab
  customsDescription: z.string().nullish(),
  countryOfOrigin: z.string().nullish(),
  seller: entityRefSchema,
  classifications: z.array(classificationEntrySchema),
  grossWeight: z.coerce.number().nullish(),
  grossWeightUnit: z.string().nullish(),
  netWeight: z.coerce.number().nullish(),
  netWeightUnit: z.string().nullish(),

  // Customs Valuation tab
  valuationMethod: z.string().nullish(),
  valuationAdjustments: z.array(valuationAdjustmentEntrySchema),

  // Packaging tab
  packagingType: z.string().nullish(),
  packagingQuantity: z.coerce.number().int().nullish(),
  shippingMarks: z.string().nullish(),

  // Regulations tab
  importControls: z.array(importControlEntrySchema),
  governmentProcedure: z.string().nullish(),
  previousProcedure: z.string().nullish(),
  additionalCode: z.string().nullish(),
});

export const goodsItemDetailSaveSchema = goodsItemDetailLiveSchema.extend({
  customsDescription: z.string().min(1, 'Customs description is required'),
  classifications: z
    .array(classificationEntrySchema)
    .min(1, 'At least one classification is required'),
});

export type GoodsItemDetailFormState = z.infer<
  typeof goodsItemDetailLiveSchema
>;
